"use client";

/**
 * 상품 옵션 선택 + 가격 계산 훅
 *
 * 다중 조합 지원: 같은 상품에서 여러 옵션 조합을 추가 (컬러 A 5개 + 컬러 B 3개)
 * 레거시 multipleOptionSelect.js 패턴을 현대적으로 재구현
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import type {
  OptionDetail,
  ProductDetailData,
  ProductOption,
} from "@/shared/api/services/productService";

import { normalizeProductOptions } from "./productOptionNormalization";

interface UseProductOptionsParams {
  product: ProductDetailData;
  options: ProductOption[];
  optionsDetails: OptionDetail[];
  /** 이미 장바구니에 담긴 해당 상품의 총 수량 */
  existingCartQuantity?: number;
}

/** 선택된 옵션 조합 1건 */
export interface SelectedOptionItem {
  id: string;
  label: string;
  codes: string[];
  optionDetailId: number;
  price: number;
  quantity: number;
  isRequired: boolean;
}

interface CartInfoItem {
  idOptionDetail: number;
  goodsCount: number;
  optionsName?: string;
}

function parseVariantList(value: string): string[] {
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed)
      ? parsed.filter((code): code is string => typeof code === "string")
      : [];
  } catch {
    return [];
  }
}

export function useProductOptions({
  product,
  options,
  optionsDetails,
  existingCartQuantity = 0,
}: UseProductOptionsParams) {
  const minOrder = product.minOrderCnt ?? 1;

  // 드롭다운 현재 선택값 (옵션 그룹별)
  const [currentSelections, setCurrentSelections] = useState<Record<number, string>>({});
  // 확정된 선택 목록 (장바구니에 담길 조합들)
  const [selectedItems, setSelectedItems] = useState<SelectedOptionItem[]>([]);

  const activeOptionsDetails = useMemo(
    () => optionsDetails.filter((od) => !od.deletedAt),
    [optionsDetails],
  );

  const normalizedOptions = useMemo(
    () => normalizeProductOptions(options, activeOptionsDetails),
    [options, activeOptionsDetails],
  );

  const visibleOptions = useMemo(() => {
    const visibleCodesByRequire = new Map<number, Set<string>>();
    for (const detail of activeOptionsDetails) {
      const codes = parseVariantList(detail.VariantList);
      if (codes.length === 0) continue;
      const key = detail.is_require;
      const visibleCodes = visibleCodesByRequire.get(key) ?? new Set<string>();
      codes.forEach((code) => visibleCodes.add(code));
      visibleCodesByRequire.set(key, visibleCodes);
    }

    return normalizedOptions
      .map((option) => {
        const visibleCodes = visibleCodesByRequire.get(option.isRequire);
        return {
          ...option,
          variant: visibleCodes
            ? option.variant.filter((variant) => visibleCodes.has(variant.code))
            : [],
        };
      })
      .filter((option) => option.variant.length > 0);
  }, [normalizedOptions, activeOptionsDetails]);

  const originalRequiredOptions = useMemo(
    () => normalizedOptions.filter((o) => o.isRequire === 1),
    [normalizedOptions],
  );
  const requiredOptions = useMemo(
    () => visibleOptions.filter((o) => o.isRequire === 1),
    [visibleOptions],
  );
  const optionalOptions = useMemo(
    () => visibleOptions.filter((o) => o.isRequire === 0),
    [visibleOptions],
  );

  const hasRequiredOptions = requiredOptions.length > 0;
  const hasOptionalOptions = optionalOptions.length > 0;
  const hasOptions =
    normalizedOptions.length > 0 && (product.is_exist_options === "Y" || hasRequiredOptions);
  // 추가옵션만 있는 상품: 본 상품 수량 + 추가옵션 별도 (레거시 OnlyDefaultSelect 패턴)
  const isOptionalOnly = hasOptions && originalRequiredOptions.length === 0 && hasOptionalOptions;

  const allRequiredSelected = requiredOptions.every((o) => currentSelections[o.id] !== undefined);

  // 옵션 조합 단위(options_details.isOnSale === 0)로 잠긴 variant 표시.
  // - 단일 필수 옵션: variant ↔ options_details 1:1 매핑이라 즉시 비활성화 가능
  // - 추가 옵션도 동일 패턴 (각 row가 단일 variant)
  // - 다중 필수 옵션 조합은 selectVariant 단계에서 모두 고를 때까지 알 수 없어 매칭 시점에 검증
  const disabledVariantReasons = useMemo(() => {
    const map = new Map<string, string>();
    const matchSingle = (variantCode: string, isRequire: 0 | 1) =>
      activeOptionsDetails.find((od) => {
        if (od.is_require !== isRequire) return false;
        const vl = parseVariantList(od.VariantList);
        return vl.length === 1 && vl[0] === variantCode;
      });
    if (requiredOptions.length === 1) {
      for (const v of requiredOptions[0].variant) {
        const matched = matchSingle(v.code, 1);
        if (matched && matched.isOnSale !== 1) map.set(v.code, "품절");
      }
    }
    for (const opt of optionalOptions) {
      for (const v of opt.variant) {
        const matched = matchSingle(v.code, 0);
        if (matched && matched.isOnSale !== 1) map.set(v.code, "품절");
      }
    }
    return map;
  }, [requiredOptions, optionalOptions, activeOptionsDetails]);

  // variant code → name 매핑
  const codeNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const opt of normalizedOptions) {
      for (const v of opt.variant) {
        map.set(v.code, v.name);
      }
    }
    return map;
  }, [normalizedOptions]);

  // 드롭다운에서 variant 선택 — 마지막 필수 옵션 선택 시 자동 추가
  const selectVariant = useCallback(
    (optionId: number, variantCode: string) => {
      setCurrentSelections((prev) => {
        const next = { ...prev, [optionId]: variantCode };

        // 모든 필수 옵션이 선택되었는지 확인
        const allSelected = requiredOptions.every((o) => next[o.id] !== undefined);
        if (allSelected && requiredOptions.length > 0) {
          // 다음 틱에서 자동 추가 (state 업데이트 후)
          setTimeout(() => autoAddRequired(next), 0);
        }

        return next;
      });
    },
    [requiredOptions],
  );

  // 필수 옵션 자동 추가 (내부용)
  const autoAddRequired = useCallback(
    (selections: Record<number, string>) => {
      const codes = requiredOptions.map((o) => selections[o.id]).filter(Boolean);
      const label = codes.map((c) => codeNameMap.get(c) ?? c).join(" / ");
      const combinedKey = [...codes].sort().join(",");

      // OptionDetail 매칭은 setSelectedItems 외부에서 먼저 검증해 toast 노출 가능
      const matched = activeOptionsDetails
        .filter((od) => {
          if (od.is_require !== 1) return false;
          const vl = parseVariantList(od.VariantList);
          return vl.every((c) => codes.includes(c));
        })
        .sort((a, b) => {
          const al = parseVariantList(a.VariantList).length;
          const bl = parseVariantList(b.VariantList).length;
          return bl - al;
        })[0];

      if (!matched) {
        toast.error("선택한 옵션 조합이 등록되어 있지 않습니다.");
        return;
      }
      if (matched.isOnSale !== 1) {
        toast.error("해당 옵션은 품절입니다.");
        return;
      }
      if (!matched.price || matched.price <= 0) {
        toast.error("해당 옵션은 가격이 등록되어 있지 않아 주문할 수 없습니다.");
        return;
      }

      // 중복 체크
      setSelectedItems((prev) => {
        if (prev.some((item) => [...item.codes].sort().join(",") === combinedKey)) return prev;

        return [
          ...prev,
          {
            id: `req-${Date.now()}`,
            label,
            codes,
            optionDetailId: matched.id,
            price: matched.price,
            quantity: 1,
            isRequired: true,
          },
        ];
      });

      // 드롭다운 초기화
      setCurrentSelections((prev) => {
        const requiredIds = new Set(requiredOptions.map((o) => o.id));
        return Object.fromEntries(
          Object.entries(prev).filter(([k]) => !requiredIds.has(Number(k))),
        );
      });
    },
    [requiredOptions, codeNameMap, activeOptionsDetails, minOrder],
  );

  // 필수 옵션 조합 확정 → selectedItems에 추가
  const addRequiredCombination = useCallback(() => {
    if (!allRequiredSelected) return;

    const codes = requiredOptions.map((o) => currentSelections[o.id]).filter(Boolean);
    const label = codes.map((c) => codeNameMap.get(c) ?? c).join(" / ");
    const combinedKey = codes.sort().join(",");

    // 중복 체크
    if (selectedItems.some((item) => item.codes.sort().join(",") === combinedKey)) {
      return "duplicate";
    }

    // OptionDetail 매칭: subset 허용 + 가장 긴 VariantList 우선 (autoAddRequired 와 동일)
    const matched = activeOptionsDetails
      .filter((od) => {
        if (od.is_require !== 1) return false;
        const vl = parseVariantList(od.VariantList);
        return vl.every((c) => codes.includes(c));
      })
      .sort((a, b) => {
        const al = parseVariantList(a.VariantList).length;
        const bl = parseVariantList(b.VariantList).length;
        return bl - al;
      })[0];

    if (!matched) return "not_found";
    if (matched.isOnSale !== 1) return "soldout";
    if (!matched.price || matched.price <= 0) return "invalid_price";

    setSelectedItems((prev) => [
      ...prev,
      {
        id: `req-${Date.now()}`,
        label,
        codes,
        optionDetailId: matched.id,
        price: matched.price,
        quantity: minOrder,
        isRequired: true,
      },
    ]);

    // 드롭다운 초기화
    setCurrentSelections((prev) => {
      const requiredIds = new Set(requiredOptions.map((o) => o.id));
      return Object.fromEntries(Object.entries(prev).filter(([k]) => !requiredIds.has(Number(k))));
    });

    return "ok";
  }, [
    allRequiredSelected,
    requiredOptions,
    currentSelections,
    codeNameMap,
    selectedItems,
    activeOptionsDetails,
    minOrder,
  ]);

  // 선택 옵션 추가
  const addOptionalItem = useCallback(
    (optionId: number, variantCode: string) => {
      // 중복 체크
      if (selectedItems.some((item) => item.codes[0] === variantCode)) {
        return "duplicate";
      }

      const matched = activeOptionsDetails.find((od) => {
        if (od.is_require !== 0) return false;
        const variantList = parseVariantList(od.VariantList);
        return variantList.includes(variantCode);
      });

      if (!matched) return "invalid_price";
      if (matched.isOnSale !== 1) return "soldout";
      // 추가옵션도 가격 정의가 없으면 차단
      if (!matched.price || matched.price <= 0) return "invalid_price";

      const name = codeNameMap.get(variantCode) ?? variantCode;

      setSelectedItems((prev) => [
        ...prev,
        {
          id: `opt-${Date.now()}`,
          label: name,
          codes: [variantCode],
          optionDetailId: matched.id,
          price: matched.price,
          quantity: 1,
          isRequired: false,
        },
      ]);

      return "ok";
    },
    [selectedItems, activeOptionsDetails, codeNameMap],
  );

  // 선택 항목 수량 변경
  const changeItemQuantity = useCallback((itemId: string, delta: number) => {
    setSelectedItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item,
      ),
    );
  }, []);

  // 선택 항목 수량 직접 설정 (input 입력용)
  const setItemQuantity = useCallback((itemId: string, quantity: number) => {
    setSelectedItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, quantity: Math.max(1, quantity) } : item,
      ),
    );
  }, []);

  // 선택 항목 삭제
  const removeItem = useCallback((itemId: string) => {
    setSelectedItems((prev) => prev.filter((item) => item.id !== itemId));
  }, []);

  // 옵션 없는 상품의 수량 (직접 관리)
  // 기존 장바구니에 이미 담겨있으면 1개부터 추가 가능
  const noOptionMin =
    existingCartQuantity >= minOrder ? 1 : Math.max(1, minOrder - existingCartQuantity);
  const [noOptionQuantity, setNoOptionQuantity] = useState(noOptionMin);
  useEffect(() => {
    setNoOptionQuantity(noOptionMin);
  }, [noOptionMin]);
  const changeNoOptionQuantity = useCallback(
    (delta: number) => setNoOptionQuantity((prev) => Math.max(noOptionMin, prev + delta)),
    [noOptionMin],
  );
  const setNoOptionQuantityDirect = useCallback(
    (quantity: number) => setNoOptionQuantity(Math.max(noOptionMin, quantity)),
    [noOptionMin],
  );

  // 총 금액
  const totalPrice = useMemo(() => {
    if (!hasOptions) return product.goodsPrice * noOptionQuantity;
    if (isOptionalOnly) {
      const optionalSum = selectedItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
      return product.goodsPrice * noOptionQuantity + optionalSum;
    }
    return selectedItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  }, [hasOptions, isOptionalOnly, product.goodsPrice, noOptionQuantity, selectedItems]);

  // 장바구니 추가용 cartInfo
  // optionsName: 사용자가 본 라벨 그대로 백엔드에 전달 → DB 정확 저장 보장
  // (백엔드는 cart.optionsName 이 있으면 OptionDetail.VariantList 로 자동 생성하는
  //  optionsName 보다 우선 사용한다)
  const buildCartInfo = useCallback((): CartInfoItem[] => {
    if (!hasOptions) {
      const noOptionDetail = activeOptionsDetails[0];
      return [{ idOptionDetail: noOptionDetail?.id ?? 0, goodsCount: noOptionQuantity }];
    }
    if (isOptionalOnly) {
      const items: CartInfoItem[] = selectedItems.map((item) => ({
        idOptionDetail: item.optionDetailId,
        goodsCount: item.quantity,
        optionsName: item.label,
      }));
      items.push({ idOptionDetail: 0, goodsCount: noOptionQuantity });
      return items;
    }
    return selectedItems.map((item) => ({
      idOptionDetail: item.optionDetailId,
      goodsCount: item.quantity,
      optionsName: item.label,
    }));
  }, [hasOptions, isOptionalOnly, activeOptionsDetails, noOptionQuantity, selectedItems]);

  const canAddToCart = !hasOptions || isOptionalOnly || selectedItems.length > 0;

  // MOQ 검증: 새로 담는 본상품/필수 옵션 수량 + 기존 장바구니의 본상품/필수 옵션 수량
  // 추가옵션은 별도 부가 상품이므로 최소 주문 수량을 채우는 기준에서 제외한다.
  const requiredSelectionQuantity = selectedItems
    .filter((item) => item.isRequired)
    .reduce((sum, item) => sum + item.quantity, 0);
  const newQuantity = !hasOptions || isOptionalOnly ? noOptionQuantity : requiredSelectionQuantity;
  const totalQuantity = newQuantity + existingCartQuantity;
  const isMoqMet = totalQuantity >= minOrder;

  return {
    // 상태
    currentSelections,
    selectedItems,
    noOptionQuantity,
    noOptionMin,
    // 분류
    requiredOptions,
    optionalOptions,
    hasOptions,
    hasRequiredOptions,
    hasOptionalOptions,
    isOptionalOnly,
    disabledVariantReasons,
    // 검증
    allRequiredSelected,
    canAddToCart,
    isMoqMet,
    totalQuantity,
    // 가격
    totalPrice,
    // 액션
    selectVariant,
    addRequiredCombination,
    addOptionalItem,
    changeItemQuantity,
    setItemQuantity,
    removeItem,
    changeNoOptionQuantity,
    setNoOptionQuantityDirect,
    buildCartInfo,
    minOrder,
  };
}
